import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Eye,
  Calendar,
  User,
  Trash2,
} from 'lucide-react';
import { adminAPI, lookupAPI, submissionsAPI } from '../../services/api';
import { toast } from 'sonner';

interface Submission {
  id: number;
  title: string;
  faculty_name: string;
  faculty_department?: string;
  department_name?: string;
  category_name: string;
  status: string;
  submitted_date: string;
  reviewed_date?: string;
}

interface Category {
  id: number;
  name: string;
}

export default function SubmissionsManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await lookupAPI.getCategories();
        if (!result.error && result.data) {
          setCategories(result.data);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminAPI.getAllSubmissions(
        searchTerm || undefined,
        statusFilter === 'all' ? undefined : statusFilter,
        categoryFilter === 'all' ? undefined : parseInt(categoryFilter)
      );
      if (!result.error && result.data) {
        const data = result.data;
        let list: Submission[] = [];

        if (Array.isArray(data) && data.length >= 2) {
          list = data[0] || [];
        } else if (Array.isArray(data)) {
          list = data;
        }

        // Some list responses may not include department fields; enrich missing rows from detail endpoint.
        const hasMissingDepartment = list.some(
          (item) => !item.faculty_department && !item.department_name
        );

        if (hasMissingDepartment) {
          const enriched = await Promise.all(
            list.map(async (item) => {
              if (item.faculty_department || item.department_name) {
                return item;
              }
              try {
                const detailResult = await submissionsAPI.getById(item.id);
                const detail = detailResult?.data;
                return {
                  ...item,
                  faculty_department:
                    detail?.faculty_department || detail?.department_name || item.faculty_department,
                };
              } catch {
                return item;
              }
            })
          );
          setSubmissions(enriched);
        } else {
          setSubmissions(list);
        }
      }
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleDeleteSubmission = async (id: number, title: string) => {
    const confirmed = window.confirm(
      `Delete submission "${title}"? This will permanently remove it from the database.`
    );
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const result = await adminAPI.deleteSubmission(id);
      if (!result?.error) {
        await fetchSubmissions();
        toast.success('Submission deleted successfully');
      } else {
        toast.error(result.message || 'Failed to delete submission.');
      }
    } catch (err) {
      console.error('Failed to delete submission:', err);
      const message = err instanceof Error ? err.message : 'Failed to delete submission.';
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredSubmissions = submissions;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-4 h-4 mr-1" />
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
            <Clock className="w-4 h-4 mr-1" />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
            <XCircle className="w-4 h-4 mr-1" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  const statusCounts = {
    all: submissions.length,
    pending: submissions.filter((s) => s.status === 'pending').length,
    approved: submissions.filter((s) => s.status === 'approved').length,
    rejected: submissions.filter((s) => s.status === 'rejected').length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Submissions Management</h1>
          <p className="text-gray-600 mt-1">Review and manage all creative work submissions</p>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 p-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { key: 'all', label: 'All', count: statusCounts.all },
              { key: 'pending', label: 'Pending', count: statusCounts.pending },
              { key: 'approved', label: 'Approved', count: statusCounts.approved },
              { key: 'rejected', label: 'Rejected', count: statusCounts.rejected },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                  statusFilter === tab.key
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, faculty, or category..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-gray-500">Loading submissions...</p>
          </div>
        ) : filteredSubmissions.length > 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Desktop View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Title
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Faculty
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Department
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Category
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Submitted
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSubmissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{submission.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-700">{submission.faculty_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-700 text-sm">
                          {submission.faculty_department || submission.department_name || 'Not specified'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          {submission.category_name}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">
                          {new Date(submission.submitted_date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(submission.status)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <Link
                            to={`/admin/submissions/${submission.id}`}
                            className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium text-sm"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDeleteSubmission(submission.id, submission.title)}
                            disabled={deletingId === submission.id}
                            className="inline-flex items-center text-red-600 hover:text-red-700 font-medium text-sm disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            {deletingId === submission.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filteredSubmissions.map((submission) => (
                <Link
                  key={submission.id}
                  to={`/admin/submissions/${submission.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 flex-1">{submission.title}</h3>
                    {getStatusBadge(submission.status)}
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      {submission.faculty_name}
                    </div>
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      {submission.faculty_department || submission.department_name || 'Not specified'}
                    </div>
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      {submission.category_name}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(submission.submitted_date).toLocaleDateString()}
                    </div>
                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteSubmission(submission.id, submission.title);
                        }}
                        disabled={deletingId === submission.id}
                        className="inline-flex items-center text-red-600 hover:text-red-700 font-medium"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {deletingId === submission.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">No submissions found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
